import type { CreateOrganizationGpuJobInput, OrganizationGpuJobDto } from "dutyon-server/types";

export type OrganizationGpuTaskState = {
  phase: "idle" | "submitting" | "watching" | "disconnected" | "terminal" | "rejected";
  input: CreateOrganizationGpuJobInput | null;
  job: OrganizationGpuJobDto | null;
  message: string | null;
  canRestore: boolean;
};
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
type Transport = {
  create(
    owner: string,
    input: CreateOrganizationGpuJobInput,
    signal: AbortSignal,
  ): Promise<OrganizationGpuJobDto>;
  get(owner: string, id: string, signal: AbortSignal): Promise<OrganizationGpuJobDto>;
  cancel(owner: string, id: string, signal: AbortSignal): Promise<OrganizationGpuJobDto>;
};
const active = (job: OrganizationGpuJobDto) => job.status === "QUEUED" || job.status === "RUNNING";
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const empty = (): OrganizationGpuTaskState => ({
  phase: "idle",
  input: null,
  job: null,
  message: null,
  canRestore: true,
});
const MAX_RECORD = 131072;
const MAX_AGE = 24 * 60 * 60 * 1000;
function freeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    for (const child of Object.values(value)) freeze(child);
    Object.freeze(value);
  }
  return value;
}

/** A tab-local recovery receipt. No bearer, nurse profiles or generated results are persisted. */
export function createOrganizationGpuTask(options: {
  owner: string;
  app: CreateOrganizationGpuJobInput["app"];
  context?: string;
  transport: Transport;
  storage?: Storage;
  now?: () => number;
}) {
  const { owner, app, transport, storage } = options;
  const now = options.now ?? Date.now;
  const context = options.context ?? "";
  if (typeof context !== "string" || context.length > 512)
    throw new Error("생성 작업 문맥을 확인해 주세요.");
  const key = `dutyon:organization-gpu:v1:${app}:${encodeURIComponent(owner)}${context ? `:${encodeURIComponent(context)}` : ""}`;
  let state = empty();
  let jobId: string | null = null;
  let createdAt = now();
  let controller: AbortController | null = null;
  let disposed = false;
  const listeners = new Set<() => void>();
  const publish = (next: OrganizationGpuTaskState) => {
    state = freeze(next);
    for (const listener of listeners) listener();
  };
  const remove = () => {
    try {
      storage?.removeItem(key);
    } catch {
      /* Unavailable tab storage is reported by canRestore. */
    }
  };
  const save = () => {
    if (!storage) return false;
    try {
      const record = JSON.stringify({
        version: 1,
        owner,
        app,
        context,
        createdAt,
        input: state.input,
        jobId,
      });
      if (record.length > MAX_RECORD) throw new Error();
      storage.setItem(key, record);
      return true;
    } catch {
      return false;
    }
  };
  if (storage) {
    try {
      const raw = storage.getItem(key);
      if (raw) {
        if (raw.length > MAX_RECORD) throw new Error();
        const saved = JSON.parse(raw);
        // Stored data is untrusted; an unknown receipt is never automatically POSTed.
        if (
          saved.version !== 1 ||
          saved.owner !== owner ||
          saved.app !== app ||
          (saved.context ?? "") !== context ||
          !Number.isSafeInteger(saved.createdAt) ||
          saved.createdAt > now() ||
          now() - saved.createdAt > MAX_AGE ||
          !saved.input ||
          saved.input.app !== app ||
          !uuid.test(saved.input.requestKey) ||
          typeof saved.input.teamId !== "string" ||
          !saved.input.teamId ||
          (saved.jobId !== null && (typeof saved.jobId !== "string" || !uuid.test(saved.jobId)))
        )
          throw new Error();
        createdAt = saved.createdAt;
        jobId = saved.jobId;
        state = {
          phase: "disconnected",
          input: saved.input,
          job: null,
          message: jobId
            ? "이전 생성 작업을 확인할 수 있습니다."
            : "접수 여부를 확인하려면 같은 요청을 재확인해 주세요.",
          canRestore: true,
        };
      }
    } catch {
      remove();
      state = empty();
    }
  }
  state = freeze(state);
  const execute = async (kind: "create" | "get" | "cancel", replacingCompletion = false) => {
    if (disposed || !state.input) return;
    if (kind !== "create" && !jobId) return;
    if (controller) {
      if (kind !== "cancel") return;
      // Cancellation must not be silently dropped behind an in-flight status poll.
      controller.abort();
      controller = null;
    }
    const input = structuredClone(state.input);
    const targetId = jobId;
    const wasAmbiguous = state.phase === "disconnected";
    const running = new AbortController();
    controller = running;
    publish({ ...state, phase: kind === "create" ? "submitting" : state.phase, message: null });
    try {
      const job =
        kind === "create"
          ? await transport.create(owner, input, running.signal)
          : kind === "get"
            ? await transport.get(owner, targetId ?? "", running.signal)
            : await transport.cancel(owner, targetId ?? "", running.signal);
      if (disposed || running.signal.aborted) return;
      if (
        !uuid.test(job.id) ||
        (jobId && job.id !== jobId) ||
        job.app !== app ||
        job.requestKey !== input.requestKey ||
        job.teamId !== input.teamId ||
        job.backend !== "metal" ||
        job.input.app !== app ||
        job.input.requestKey !== input.requestKey ||
        job.input.teamId !== input.teamId ||
        job.input.previousJobId !== input.previousJobId?.toLowerCase() ||
        job.input.sourceRequestId !== input.sourceRequestId ||
        job.input.sourceResponseId !== input.sourceResponseId ||
        !["QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "TIMED_OUT", "CANCELLED"].includes(
          job.status,
        ) ||
        (job.status === "SUCCEEDED" && !job.result)
      )
        throw new Error();
      jobId = job.id;
      // Use server-normalized planning choices for restored previews.
      const next = {
        phase: active(job) ? "watching" : "terminal",
        input: structuredClone(job.input),
        job: structuredClone(job),
        message: null,
        canRestore: state.canRestore,
      } as OrganizationGpuTaskState;
      state = next;
      next.canRestore = save();
      publish(next);
      return structuredClone(job);
    } catch (error) {
      const response = (
        error as { response?: { status?: number; data?: { code?: string } } } | null
      )?.response;
      const status = response?.status;
      const refusedBeforeAdmission =
        kind === "create" &&
        !targetId &&
        !wasAmbiguous &&
        status === 503 &&
        ([
          "ORGANIZATION_GPU_DISABLED",
          "ORGANIZATION_GPU_UNAVAILABLE",
          "ORGANIZATION_GPU_NOT_CONFIGURED",
        ].includes(response?.data?.code ?? "") ||
          (replacingCompletion &&
            response?.data?.code === "ORGANIZATION_GPU_CONTINUATION_DISABLED"));
      if (
        !disposed &&
        !running.signal.aborted &&
        ([400, 401, 403, 404, 409, 422, 429].includes(status ?? 0) || refusedBeforeAdmission)
      ) {
        remove();
        publish({
          ...state,
          phase: "rejected",
          job: null,
          message: refusedBeforeAdmission
            ? response?.data?.code === "ORGANIZATION_GPU_CONTINUATION_DISABLED"
              ? "기간 이어 생성을 사용할 수 없습니다. 작업을 확인한 뒤 생성 조건을 다시 선택해 주세요."
              : "Mac GPU 생성을 사용할 수 없습니다. 잠시 후 다시 시도하거나 관리자에게 설정·연결 상태 확인을 요청해 주세요."
            : "생성 요청이나 현재 접근 권한을 확인해 주세요.",
        });
        return;
      }
      if (!disposed && !running.signal.aborted)
        publish({
          ...state,
          phase: "disconnected",
          message: jobId
            ? "연결이 끊겼습니다. 기존 작업을 다시 확인해 주세요."
            : "접수 응답을 확인하지 못했습니다. 같은 요청을 재확인해 주세요.",
        });
    } finally {
      if (controller === running) controller = null;
    }
  };
  const begin = async (input: CreateOrganizationGpuJobInput, replacingCompletion = false) => {
    if (input.app !== app || !uuid.test(input.requestKey))
      throw new Error("생성 요청을 확인해 주세요.");
    createdAt = now();
    jobId = null;
    state = { ...empty(), input: structuredClone(input) };
    state.canRestore = save();
    await execute("create", replacingCompletion);
  };
  return {
    getSnapshot: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async start(input: CreateOrganizationGpuJobInput) {
      if (disposed || controller || state.phase !== "idle")
        throw new Error("기존 생성 작업을 먼저 확인해 주세요.");
      await begin(input);
    },
    /** Replace a confirmed completion with its explicit successor, never an unrelated request. */
    async startContinuation(input: CreateOrganizationGpuJobInput) {
      const previous = state.job;
      if (
        disposed ||
        controller ||
        state.phase !== "terminal" ||
        previous?.status !== "SUCCEEDED" ||
        !previous.result ||
        input.previousJobId?.toLowerCase() !== previous.id ||
        input.teamId !== previous.teamId ||
        input.sourceRequestId !== previous.input.sourceRequestId ||
        input.sourceResponseId !== previous.input.sourceResponseId ||
        input.app !== previous.app ||
        input.requestKey === previous.requestKey
      )
        throw new Error("앞선 완료 작업을 다시 확인해 주세요.");
      // Keep the prior receipt until the new input is validated; save the successor before POST.
      await begin(input, true);
    },
    // Only explicit retry may re-POST an ambiguous receipt, always with its original key/input.
    retry: () => execute(jobId ? "get" : "create"),
    /** A confirmed failed execution needs a new key; ambiguous requests must use retry instead. */
    async retryFailed() {
      if (disposed || controller) return;
      if (
        state.phase !== "terminal" ||
        !state.input ||
        !state.job ||
        !["FAILED", "TIMED_OUT"].includes(state.job.status)
      )
        throw new Error("기존 작업의 실패 상태를 먼저 확인해 주세요.");
      await begin({ ...structuredClone(state.input), requestKey: crypto.randomUUID() });
    },
    refresh: () => execute("get"),
    cancel: () => execute("cancel"),
    clear() {
      if (
        disposed ||
        controller ||
        (state.phase !== "terminal" && state.phase !== "idle" && state.phase !== "rejected")
      )
        throw new Error("진행 중이거나 접수 여부가 불명확한 작업을 먼저 확인해 주세요.");
      remove();
      jobId = null;
      publish(empty());
    },
    dispose(clearReceipt = false) {
      disposed = true;
      controller?.abort();
      controller = null;
      if (clearReceipt) remove();
      state = empty();
      listeners.clear();
    },
  };
}
